import { type MouseEvent, memo, useCallback } from 'react';

import { CheckIcon } from '@air/design-icons';
import { tailwindVariants } from '@air/utils-tailwind';

import { workspaceAgentChoiceChipMark } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentChoiceChip';

const workspaceAgentFilterRow = tailwindVariants({
  base: 'flex min-h-7 w-full cursor-pointer items-center gap-2 rounded-8 px-2 py-1 text-left text-12 leading-4 transition-colors',
  compoundVariants: [{ class: 'bg-gray-2', isHighlighted: true, pickedSingle: false }],
  defaultVariants: {
    isHighlighted: false,
    isKeyboardHighlighted: false,
    picked: false,
    pickedSingle: false,
  },
  variants: {
    isHighlighted: {
      false: '',
      true: '',
    },
    isKeyboardHighlighted: {
      false: '',
      true: 'ring-2 ring-altitude-9 ring-inset',
    },
    picked: {
      false: 'text-gray-11',
      true: 'font-semibold text-gray-12',
    },
    pickedSingle: {
      false: 'hover:bg-gray-2',
      true: 'bg-gray-3',
    },
  },
});

const preventFocusSteal = (event: MouseEvent<HTMLDivElement>) => {
  event.preventDefault();
};

export type WorkspaceAgentFilterOptionProps = {
  domId: string;
  index: number;
  isHighlighted: boolean;
  isKeyboardHighlighted: boolean;
  label: string;
  multiple: boolean;
  onHighlight: (index: number) => void;
  onSelect: (index: number) => void;
  selected: boolean;
};

export const WorkspaceAgentFilterOption = memo(
  ({
    domId,
    index,
    isHighlighted,
    isKeyboardHighlighted,
    label,
    multiple,
    onHighlight,
    onSelect,
    selected,
  }: WorkspaceAgentFilterOptionProps) => {
    const handleClick = useCallback(() => {
      onSelect(index);
    }, [index, onSelect]);
    const handleMouseEnter = useCallback(() => {
      onHighlight(index);
    }, [index, onHighlight]);

    return (
      // biome-ignore lint/a11y/useFocusableInteractive: combobox options stay unfocusable; the search field owns focus via aria-activedescendant
      // biome-ignore lint/a11y/useKeyWithClickEvents: arrow keys and Enter in the search field pick options
      <div // NOSONAR -- combobox option; the search field owns focus via aria-activedescendant
        aria-selected={selected}
        className={workspaceAgentFilterRow({
          isHighlighted,
          isKeyboardHighlighted,
          picked: selected,
          pickedSingle: selected && !multiple,
        })}
        data-option-index={index}
        data-testid="WORKSPACE_AGENT_FILTER_OPTION"
        id={domId}
        onClick={handleClick}
        onMouseDown={preventFocusSteal}
        onMouseEnter={handleMouseEnter}
        role="option"
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {multiple ? (
          <span aria-hidden className={workspaceAgentChoiceChipMark({ checked: selected })}>
            {selected ? <CheckIcon className="size-2" /> : null}
          </span>
        ) : null}
        {!multiple && selected ? <CheckIcon aria-hidden className="size-3.5 shrink-0 text-altitude-9" /> : null}
      </div>
    );
  },
);

WorkspaceAgentFilterOption.displayName = 'WorkspaceAgentFilterOption';
